import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { assetTracksApi } from '../apis/assetTracksApi';

export const MAX_ASSET_TRACKS = 5;

export interface AssetTrackSeed {
  assetId: number;
  assetName: string;
  startTime: string;
  endTime: string;
}

export interface AssetTrackRecord extends AssetTrackSeed {
  id: string; // Constructed from the asset ID, start and end time to avoid duplication of the same track.
  visible: boolean;
}

interface AssetTracksState {
  trackIds: string[];
  tracks: Record<string, AssetTrackRecord>;
  viewTracksRequestTimestamp: number;
}

interface AssetTrackAction {
  trackId: string;
}

// Create a unique id for the requested track properties.
export const getTrackRecordId = ({
  assetId,
  startTime,
  endTime,
}: Omit<AssetTrackSeed, 'assetName'>): string => {
  return `${assetId}_${startTime}_${endTime}`;
};

const initialData: AssetTracksState = {
  trackIds: [],
  tracks: {},
  viewTracksRequestTimestamp: 0,
};

const assetTracksSlice = createSlice({
  name: 'assetTracks',
  initialState: initialData,
  reducers: {
    toggleTrackVisibility: (state, { payload }: PayloadAction<AssetTrackAction>) => {
      const track = state.tracks[payload.trackId];
      if (typeof track !== 'undefined') {
        state.tracks[payload.trackId].visible = !track.visible;
      }
    },
    setTracks: (state, { payload }: PayloadAction<AssetTrackSeed[]>) => {
      for (let i = 0; i < payload.length; i++) {
        const seed = payload[i];
        const trackId = getTrackRecordId(seed);

        // Confirm that the track request details do not already exist in the store before adding them.
        if (typeof state.tracks[trackId] === 'undefined' && !state.trackIds.includes(trackId)) {
          // Add new record to the list of track ids
          state.trackIds = [trackId, ...state.trackIds];
          // Add the new record object to the tracks map
          state.tracks[trackId] = {
            ...seed,
            id: trackId,
            visible: false, // Set to false by default, then make visible if a request succeeds with valid data.
          };
        }
      }
    },
    removeTrack: (state, { payload }: PayloadAction<AssetTrackAction>) => {
      state.trackIds = state.trackIds.filter(id => id !== payload.trackId);
      delete state.tracks[payload.trackId];
    },
    requestViewTracks: state => {
      state.viewTracksRequestTimestamp = Date.now();
    },
    clearTracks: () => ({
      ...initialData,
    }),
  },
  extraReducers: builder => {
    // Handle new track requests that succeed.
    builder.addMatcher(assetTracksApi.endpoints.getAssetTracks.matchFulfilled, (state, action) => {
      const trackId = action.meta.arg.originalArgs.trackId;
      const hasTrackData = action.payload.tracks.length > 0;

      if (typeof state.tracks[trackId] !== 'undefined') {
        const isVisible = state.tracks[trackId].visible;
        if (hasTrackData && !isVisible) {
          state.tracks[trackId].visible = true;
        } else if (!hasTrackData && isVisible) {
          state.tracks[trackId].visible = false;
        }
      }
    });
    // Handle request rejection (make certain that the associated track is not visible).
    builder.addMatcher(assetTracksApi.endpoints.getAssetTracks.matchRejected, (state, action) => {
      const trackId = action.meta.arg.originalArgs.trackId;
      // Make sure that the rejected track is not visible.
      if (typeof state.tracks[trackId] !== 'undefined' && state.tracks[trackId].visible) {
        state.tracks[trackId].visible = false;
      }
    });
  },
});

export const { toggleTrackVisibility, setTracks, removeTrack, clearTracks, requestViewTracks } =
  assetTracksSlice.actions;

export default assetTracksSlice.reducer;
